import { useState, useEffect, useRef } from 'react';
import { galleryData } from '../data/galleryData';
import { createStandardGalleryConfig } from './usePageTemplate';
import { getImageUrl } from '../utils/imageConfig';

/**
 * Standardized gallery hook that provides consistent photo discovery,
 * error handling, and configuration across all gallery pages
 * 
 * @param {string} galleryType - Type of gallery (e.g., 'futbol', 'rossetti')
 * @param {Object} [customConfig] - Custom configuration overrides
 * @returns {Object} Gallery state and utilities
 */
export const useStandardGallery = (galleryType, customConfig = {}) => {
  const galleryRef = useRef();
  const [photos, setPhotos] = useState(galleryData[galleryType] || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create standardized gallery configuration
  const galleryConfig = createStandardGalleryConfig(galleryType, customConfig);

  // Standardized photo loading with consistent error handling
  useEffect(() => {
    const loadPhotos = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const staticPhotos = galleryData[galleryType] || [];
        // Process photos to use environment-aware URLs
        const processedPhotos = staticPhotos.map(photo => ({
          ...photo,
          src: getImageUrl(photo.src),
          // Add fallback URLs for better error handling
          fallbackSrc: `https://marcodemarco.net${photo.src}`
        }));
        setPhotos(processedPhotos);
        console.log(`Successfully loaded ${processedPhotos.length} photos for ${galleryType} gallery`);
      } catch (err) {
        console.warn(`Could not load photos for ${galleryType}:`, err);
        setError(err.message);
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    };

    loadPhotos();
  }, [galleryType]);

  // Refresh function for manual photo reload
  const refreshPhotos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const staticPhotos = galleryData[galleryType] || [];
      // Process photos to use environment-aware URLs
      const processedPhotos = staticPhotos.map(photo => ({
        ...photo,
        src: getImageUrl(photo.src),
        fallbackSrc: `https://marcodemarco.net${photo.src}`
      }));
      setPhotos(processedPhotos);
    } catch (err) {
      console.warn(`Could not refresh photos for ${galleryType}:`, err);
      setError(err.message);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  return {
    // Gallery state
    photos,
    loading,
    error,
    
    // Gallery configuration
    galleryConfig,
    
    // Gallery ref for external control
    galleryRef,
    
    // Utilities
    refreshPhotos,
    
    // Standardized props for UnifiedGallery component
    getGalleryProps: (title, additionalProps = {}) => ({
      ref: galleryRef,
      images: photos,
      title,
      type: galleryType,
      features: galleryConfig.features,
      layout: galleryConfig.layout,
      ...additionalProps
    })
  };
};

export default useStandardGallery;